import isEmpty from 'lodash/isEmpty';
import { useEffect, useMemo, useState } from 'react';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { FaRegEye } from 'react-icons/fa';
import { NEXT_PUBLIC_DEFAULT_COUNT_VIEW } from 'src/constants';
import { useCountviews } from 'src/utils/countview';
import { convertBadge, numeralFormat, timestamp, useDateUpdate } from 'src/utils/helper';
import styled from 'styled-components';
import Image, { IMAGE_SIZE } from '../image';
import Link from '../link';

export default function CardByLine({
  data,
  showH2 = false,
  className = '',
  showBadge = true,
  showTitle = true,
  showBlurb = true,
  showByline = false,
  showImage = true,
  showPublishDate = false,
  showCountView = false,
  showFooter = false,
  imageLoading,
  imageDecoding,
  imageFetchPriority,
  imageWidth,
  imageHeight,
  eventDataLayer = {},
  external = false,
  path = '',
  size
}) {
  if (isEmpty(data)) return null;
  const [countView, setCountView] = useState(0);

  const conditionCountview = showCountView && Number(countView) > Number(NEXT_PUBLIC_DEFAULT_COUNT_VIEW);
  const conditionBadge = showBadge && convertBadge(data, false);

  useEffect(() => {
    (async () => {
      try {
        const _pageviews = await useCountviews(data);
        setCountView(_pageviews);
      } catch (err) {
        console.error(`${timestamp()} ==========> CARD_FULL ERROR :`, err.message);
      }
    })();
  }, [data]);
  const href = useMemo(() => {
    return `${data?.link || path}`;
  }, [data, path]);
  return (
    <CardWrapper className={`card-byline ${className}`}>
      {/* ! DATALAYER — ส่งจุดเดียวที่ <Link> (เดิม wrapper ยิงซ้ำ และ <Link> ยิงก้อนว่างเพราะไม่ได้รับ eventDataLayer) */}
      <Link title={data?.title} href={href} tabIndex={-1} eventDataLayer={eventDataLayer}>
        {showImage && (
          <div className='card-image'>
            <Image
              external={external}
              src={data?.image}
              alt={data?.title}
              title={data?.title}
              size={size ?? IMAGE_SIZE.MD_WEBP}
              width={imageWidth}
              height={imageHeight}
              loading={imageLoading}
              decoding={imageDecoding}
              fetchPriority={imageFetchPriority}
            />
          </div>
        )}
        <div className='card-content'>
          {showTitle && (showH2 === true ? <h2 className='card-title text-excerpt-2'>{data?.title}</h2> : <h3 className='card-title text-excerpt-2'>{data?.title}</h3>)}
          {showBlurb && <p className='card-blurb text-excerpt-3'>{data?.blurb}</p>}
        </div>
        {showFooter && (
          <div className='card-footer flex-between'>
            <div className='footer-left'>
              {showByline && <>{data.byline && <p className='byline'>{`By [${data.byline}]`}</p>}</>}
              {conditionCountview && (
                <div className='count-view'>
                  <FaRegEye size={16} />
                  <small>{numeralFormat(countView)}</small>
                </div>
              )}
            </div>
          </div>
        )}
        {showPublishDate && (
          <div className='footer --item date-publish'>
            {conditionBadge && <span className='meta-badge'>{conditionBadge}</span>}
            <small>
              <AiOutlineClockCircle size={16} className='date-publish--icon' />
              {data?.published_at && useDateUpdate(data.published_at, true)}
            </small>
          </div>
        )}
      </Link>
    </CardWrapper>
  );
}

const CardWrapper = styled.div`
  position: relative;
  :hover {
    img {
      transform: scale(1.15);
    }
  }
  .card-title {
    font-size: 22pt;
    font-weight: 500;
    line-height: 1;
    margin: 10px 0;
    letter-spacing: 0;
    font-family: 'DBHelvethaicaXBd', sans-serif;
    color: var(--text-color);
    min-height: 56px;
    /* height: 56px; */
    @media (max-width: 767px) {
      margin: 10px 0px 0px;
      font-size: 20pt;
      min-height: 64px;
      -webkit-line-clamp: 3 !important;
    }
  }
  .card-image {
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      aspect-ratio: 16 / 9;
      transition: all 1s ease-out;
    }
  }
  .card-blurb {
    font-size: 20pt;
    font-weight: 300;
    color: var(--primary-gray);
    line-height: 1.15;
    font-family: 'DBHelvethaica', sans-serif;
  }
  .meta-badge {
    padding: 2px 10px;
    background-color: var(--secondary-gray);
    color: var(--white-color);
    font-size: 16pt;
    border-radius: 0 8px 0 8px;
    margin-right: 10px;
  }
  .byline {
    font-size: 12pt;
    font-weight: 300;
    color: var(--black-color);
    margin-top: 15px;
  }
  .footer {
    margin-top: 10px;
  }
  .date-publish {
    &--icon {
      margin-right: 4px;
      margin-bottom: 2px;
    }
    &--icon,
    small {
      text-transform: lowercase;
      font-size: 16pt;
      font-weight: 200;
      line-height: 1;
      color: #707070;
    }
  }
`;
